import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  loadImage,
  fileToDataURL,
  validateImageFile,
  concatenateImages,
  trimImageVertical,
} from '../utils/imageUtils';
import { useI18n } from '../contexts/I18nContext';
import '../styles/components/ImageConcatModal.css';

interface ImageItem {
  id: string;
  file: File;
  dataUrl: string;
  width: number;
  height: number;
}

interface ImageConcatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (concatenatedImageDataUrl: string, width: number, height: number) => void;
}

const ImageConcatModal: React.FC<ImageConcatModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();
  const [images, setImages] = useState<ImageItem[]>([]);
  const [targetWidth, setTargetWidth] = useState<number>(800);
  const [trimVertical, setTrimVertical] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 모달이 열릴 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setImages([]);
      setTargetWidth(800);
      setTrimVertical(false);
      setError(null);
      setPreviewDataUrl(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // 이미지가 변경될 때마다 미리보기 업데이트
  useEffect(() => {
    if (images.length === 0) {
      setPreviewDataUrl(null);
      return;
    }

    const updatePreview = async () => {
      try {
        const loadedImages = await Promise.all(
          images.map(item => loadImage(item.dataUrl))
        );
        const resultDataUrl = concatenateImages(loadedImages, targetWidth, trimVertical);
        setPreviewDataUrl(resultDataUrl);
      } catch (err) {
        console.error('Preview generation failed:', err);
      }
    };

    updatePreview();
  }, [images, targetWidth, trimVertical]);

  // 파일 선택 처리
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);
    setIsProcessing(true);

    try {
      const newImages: ImageItem[] = [];

      for (const file of Array.from(files)) {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          setError(validation.error || t.common.error);
          continue;
        }

        const dataUrl = await fileToDataURL(file);
        const img = await loadImage(dataUrl);

        newImages.push({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          dataUrl,
          width: img.width,
          height: img.height,
        });
      }

      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setIsProcessing(false);
    }
  }, [t]);

  // 이미지 순서 변경 (위로)
  const moveUp = useCallback((index: number) => {
    if (index <= 0) return;
    setImages(prev => {
      const newImages = [...prev];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      return newImages;
    });
  }, []);

  // 이미지 순서 변경 (아래로)
  const moveDown = useCallback((index: number) => {
    setImages(prev => {
      if (index >= prev.length - 1) return prev;
      const newImages = [...prev];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      return newImages;
    });
  }, []);

  // 이미지 제거
  const removeImage = useCallback((id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  }, []);

  // 확인 버튼 처리
  const handleConfirm = useCallback(async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      const loadedImages = await Promise.all(
        images.map(item => loadImage(item.dataUrl))
      );

      const resultDataUrl = concatenateImages(loadedImages, targetWidth, trimVertical);

      // 결과 이미지 크기 계산
      const scaledHeights = loadedImages.map(img => {
        const imgHeight = trimVertical ? trimImageVertical(img).height : img.height;
        const scale = targetWidth / img.width;
        return imgHeight * scale;
      });
      const totalHeight = scaledHeights.reduce((sum, h) => sum + h, 0);

      onConfirm(resultDataUrl, targetWidth, totalHeight);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setIsProcessing(false);
    }
  }, [images, targetWidth, trimVertical, onConfirm, onClose, t]);

  // 키보드 이벤트 처리
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isProcessing) {
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isProcessing && images.length > 0) {
      e.preventDefault();
      handleConfirm();
    }
  }, [isProcessing, onClose, handleConfirm, images.length]);

  // 드래그 앤 드롭 처리
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  if (!isOpen) return null;

  return (
    <div className="image-concat-modal-overlay" onClick={isProcessing ? undefined : onClose}>
      <div
        className="image-concat-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* 헤더 */}
        <div className="image-concat-modal-header">
          <h2 className="image-concat-modal-title">{t.modals.imageConcat.title}</h2>
          {!isProcessing && (
            <button className="image-concat-modal-close" onClick={onClose} title={t.modals.imageConcat.close}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 5L5 15M5 5l10 10" />
              </svg>
            </button>
          )}
        </div>

        {/* 본문 */}
        <div className="image-concat-modal-body">
          {/* 파일 선택 영역 */}
          <div
            className="image-concat-drop-zone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              onChange={(e) => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="8" y="8" width="32" height="32" rx="4" />
              <path d="M16 32l6-8 4 5 6-10 8 13" />
              <circle cx="18" cy="18" r="3" />
            </svg>
            <p className="image-concat-drop-text">
              {t.modals.imageConcat.dragDropText}
            </p>
            <p className="image-concat-drop-hint">
              {t.modals.imageConcat.supportedFormats}
            </p>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="image-concat-error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="8" cy="8" r="6" />
                <path d="M8 5v3M8 11h.01" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* 이미지 목록 */}
          {images.length > 0 && (
            <div className="image-concat-list">
              <div className="image-concat-list-header">
                <span className="image-concat-list-title">{t.modals.imageConcat.selectedImages} ({images.length})</span>
                <button
                  className="image-concat-clear-btn"
                  onClick={() => setImages([])}
                  disabled={isProcessing}
                >
                  {t.modals.imageConcat.clearAll}
                </button>
              </div>
              <div className="image-concat-items">
                {images.map((item, index) => (
                  <div key={item.id} className="image-concat-item">
                    <span className="image-concat-item-order">{index + 1}</span>
                    <img
                      src={item.dataUrl}
                      alt={item.file.name}
                      className="image-concat-item-thumb"
                    />
                    <div className="image-concat-item-info">
                      <span className="image-concat-item-name" title={item.file.name}>
                        {item.file.name}
                      </span>
                      <span className="image-concat-item-size">
                        {item.width} x {item.height}
                      </span>
                    </div>
                    <div className="image-concat-item-actions">
                      <button
                        className="image-concat-item-btn"
                        onClick={() => moveUp(index)}
                        disabled={index === 0 || isProcessing}
                        title={t.modals.imageConcat.moveUp}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 4v8M4 7l4-4 4 4" />
                        </svg>
                      </button>
                      <button
                        className="image-concat-item-btn"
                        onClick={() => moveDown(index)}
                        disabled={index === images.length - 1 || isProcessing}
                        title={t.modals.imageConcat.moveDown}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 12V4M4 9l4 4 4-4" />
                        </svg>
                      </button>
                      <button
                        className="image-concat-item-btn image-concat-item-btn-danger"
                        onClick={() => removeImage(item.id)}
                        disabled={isProcessing}
                        title={t.modals.imageConcat.delete}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 4L4 12M4 4l8 8" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 옵션 설정 */}
          {images.length > 0 && (
            <div className="image-concat-options">
              <div className="image-concat-option">
                <label className="image-concat-option-label">{t.modals.imageConcat.resultWidth}</label>
                <div className="image-concat-width-input">
                  <input
                    type="range"
                    min="200"
                    max="1920"
                    step="10"
                    value={targetWidth}
                    onChange={(e) => setTargetWidth(parseInt(e.target.value))}
                    className="image-concat-range"
                    disabled={isProcessing}
                  />
                  <input
                    type="number"
                    min="200"
                    max="1920"
                    value={targetWidth}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value >= 200 && value <= 1920) {
                        setTargetWidth(value);
                      }
                    }}
                    className="image-concat-number"
                    disabled={isProcessing}
                  />
                  <span className="image-concat-unit">px</span>
                </div>
              </div>
              <div className="image-concat-option">
                <label className="image-concat-checkbox-label">
                  <input
                    type="checkbox"
                    checked={trimVertical}
                    onChange={(e) => setTrimVertical(e.target.checked)}
                    disabled={isProcessing}
                    className="image-concat-checkbox"
                  />
                  <span>{t.modals.imageConcat.trimMargins}</span>
                  <span className="image-concat-option-hint">{t.modals.imageConcat.trimDescription}</span>
                </label>
              </div>
            </div>
          )}

          {/* 미리보기 */}
          {previewDataUrl && (
            <div className="image-concat-preview">
              <div className="image-concat-preview-header">
                <span className="image-concat-preview-title">{t.modals.imageConcat.preview}</span>
              </div>
              <div className="image-concat-preview-container">
                <img
                  src={previewDataUrl}
                  alt="Preview"
                  className="image-concat-preview-image"
                />
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="image-concat-modal-footer">
          <button
            className="image-concat-btn image-concat-btn-secondary"
            onClick={onClose}
            disabled={isProcessing}
          >
            {t.modals.imageConcat.cancel}
          </button>
          <button
            className="image-concat-btn image-concat-btn-primary"
            onClick={handleConfirm}
            disabled={isProcessing || images.length === 0}
          >
            {isProcessing ? t.modals.imageConcat.processing : t.modals.imageConcat.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageConcatModal;
